(() => {
  const ready = (fn) => document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn();
  const normalize = (value) => (value || '').toLocaleLowerCase(document.documentElement.lang || undefined).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobileViewport = () => window.matchMedia('(max-width: 749px)').matches;

  ready(() => {
    document.querySelectorAll('[data-awj-archive]').forEach((archive) => {
      if (archive.dataset.awjReady === 'true') return;
      archive.dataset.awjReady = 'true';

      const input = archive.querySelector('[data-awj-search]');
      const filters = [...archive.querySelectorAll('[data-awj-filter]')];
      const panels = [...archive.querySelectorAll('[data-awj-panel]')];
      const list = archive.querySelector('[data-awj-index-list]');
      const navigator = archive.querySelector('[data-awj-navigator]');
      const pickerToggle = archive.querySelector('[data-awj-picker-toggle]');
      const activeName = archive.querySelector('[data-awj-active-name]');
      const activeCategoryLabel = archive.querySelector('[data-awj-active-category]');
      const categoryNote = archive.querySelector('[data-awj-category-note]');
      const empty = archive.querySelector('[data-awj-empty]');
      const reset = archive.querySelector('[data-awj-reset]');
      const visibleCount = archive.querySelector('[data-awj-visible-count]');
      const resultWord = archive.querySelector('[data-awj-result-word]');
      const totalCount = archive.querySelector('[data-awj-total-count]');
      const position = archive.querySelector('[data-awj-position]');
      const previous = archive.querySelector('[data-awj-prev]');
      const next = archive.querySelector('[data-awj-next]');
      const stage = archive.querySelector('[data-awj-stage]');
      const rootEl = document.documentElement;
      const labelSingle = archive.dataset.awjLabelSingle || 'Stoff';
      const labelPlural = archive.dataset.awjLabelPlural || 'Stoffe';
      const labelOf = archive.dataset.awjLabelOf || 'von';
      if (!panels.length || !list) return;

      const terms = panels.map((panel, indexValue) => ({
        id: panel.dataset.awjPanel,
        name: panel.dataset.awjName || '',
        inci: panel.dataset.awjInci || '',
        category: panel.dataset.awjCategory || '',
        categoryLabel: panel.dataset.awjCategoryLabel || '',
        search: normalize(`${panel.dataset.awjKeywords || ''} ${panel.textContent || ''}`),
        number: indexValue + 1,
        panel,
      }));

      let activeCategory = 'all';
      let activeId = terms[0].id;
      let matches = [...terms];
      const itemButtons = new Map();
      const twoDigits = (value) => String(value).padStart(2, '0');
      const isMobile = () => mobileViewport();

      const createIndexItem = (term) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'awj-index-item';
        button.dataset.awjTarget = term.id;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', term.id === activeId ? 'true' : 'false');
        button.setAttribute('aria-controls', `stoff-${term.id}`);
        const number = document.createElement('span');
        number.className = 'awj-index-item__no';
        number.textContent = twoDigits(term.number);
        const name = document.createElement('span');
        name.className = 'awj-index-item__name';
        name.textContent = term.name;
        const inci = document.createElement('span');
        inci.className = 'awj-index-item__inci';
        inci.textContent = `INCI · ${term.inci}`;
        const category = document.createElement('span');
        category.className = 'awj-index-item__category';
        category.textContent = term.categoryLabel;
        button.append(number, name, inci, category);
        return button;
      };

      terms.forEach((term) => {
        const button = createIndexItem(term);
        list.appendChild(button);
        itemButtons.set(term.id, button);
      });
      if (totalCount) totalCount.textContent = String(terms.length);

      const currentFilter = () => filters.find((button) => button.dataset.awjFilter === activeCategory);
      const syncSelected = () => itemButtons.forEach((button, id) => button.setAttribute('aria-selected', id === activeId ? 'true' : 'false'));
      const syncPosition = () => {
        const selected = terms.find((term) => term.id === activeId);
        if (activeName && selected) activeName.textContent = selected.name;
        if (activeCategoryLabel && selected) activeCategoryLabel.textContent = selected.categoryLabel;
        if (!position) return;
        const index = matches.findIndex((term) => term.id === activeId);
        if (index >= 0 && matches.length) position.textContent = `${twoDigits(index + 1)} ${labelOf} ${twoDigits(matches.length)}`;
        else position.textContent = `${twoDigits(selected?.number || 1)} ${labelOf} ${twoDigits(terms.length)}`;
      };

      const closePicker = ({ returnFocus = false } = {}) => {
        if (!navigator || !pickerToggle || !isMobile()) return;
        navigator.classList.remove('is-open');
        pickerToggle.setAttribute('aria-expanded', 'false');
        if (returnFocus) pickerToggle.focus({ preventScroll: true });
      };
      const openPicker = () => {
        if (!navigator || !pickerToggle || !isMobile()) return;
        navigator.classList.add('is-open');
        pickerToggle.setAttribute('aria-expanded', 'true');
      };

      const selectTerm = (id, options = {}) => {
        const selected = terms.find((term) => term.id === id);
        if (!selected) return;
        activeId = selected.id;
        panels.forEach((panel) => {
          const chosen = panel === selected.panel;
          panel.hidden = !chosen;
          panel.classList.toggle('is-active', chosen);
        });
        selected.panel.classList.remove('is-revealing');
        if (options.animate !== false && !reducedMotion()) {
          requestAnimationFrame(() => {
            selected.panel.classList.add('is-revealing');
            selected.panel.addEventListener('animationend', () => selected.panel.classList.remove('is-revealing'), { once: true });
          });
        }
        syncSelected();
        syncPosition();
        const selectedButton = itemButtons.get(selected.id);
        selectedButton?.scrollIntoView({ block: 'nearest' });
        if (options.closePicker) closePicker();
        if (options.scroll && isMobile() && stage) {
          window.setTimeout(() => stage.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }), 30);
        }
      };

      const render = ({ selectFirst = false } = {}) => {
        const query = normalize(input?.value || '');
        matches = terms.filter((term) => (activeCategory === 'all' || term.category === activeCategory) && (!query || term.search.includes(query)));
        itemButtons.forEach((button, id) => { button.hidden = !matches.some((term) => term.id === id); });
        if (visibleCount) visibleCount.textContent = String(matches.length);
        if (resultWord) resultWord.textContent = matches.length === 1 ? labelSingle : labelPlural;
        if (empty) empty.hidden = matches.length !== 0;
        if (reset) reset.hidden = !(query || activeCategory !== 'all');
        if (categoryNote) categoryNote.textContent = currentFilter()?.dataset.awjDescription || '';
        if (previous) previous.disabled = matches.length <= 1;
        if (next) next.disabled = matches.length <= 1;
        if (!matches.length) { syncPosition(); return; }
        const stillVisible = matches.some((term) => term.id === activeId);
        if (selectFirst || !stillVisible) selectTerm(matches[0].id, { animate: true });
        else { syncSelected(); syncPosition(); }
      };

      const resetArchive = ({ focusSearch = false } = {}) => {
        activeCategory = 'all';
        if (input) input.value = '';
        filters.forEach((button) => button.setAttribute('aria-pressed', button.dataset.awjFilter === 'all' ? 'true' : 'false'));
        render();
        if (focusSearch) input?.focus({ preventScroll: true });
      };

      itemButtons.forEach((button, id) => {
        button.addEventListener('click', () => selectTerm(id, { closePicker: true, scroll: true }));
        button.addEventListener('keydown', (event) => {
          const visibleButtons = matches.map((term) => itemButtons.get(term.id)).filter(Boolean);
          const current = visibleButtons.indexOf(button);
          let target = -1;
          if (event.key === 'ArrowDown') target = Math.min(visibleButtons.length - 1, current + 1);
          if (event.key === 'ArrowUp') target = Math.max(0, current - 1);
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = visibleButtons.length - 1;
          if (target >= 0) { event.preventDefault(); visibleButtons[target]?.focus(); }
        });
      });

      input?.addEventListener('input', () => render({ selectFirst: true }), { passive: true });
      input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && matches.length) { event.preventDefault(); selectTerm(matches[0].id, { closePicker: true, scroll: true }); input.blur(); }
        if (event.key === 'Escape') { event.preventDefault(); resetArchive(); input.blur(); closePicker({ returnFocus: true }); }
      });
      filters.forEach((button) => button.addEventListener('click', () => {
        activeCategory = button.dataset.awjFilter || 'all';
        filters.forEach((item) => item.setAttribute('aria-pressed', item === button ? 'true' : 'false'));
        render({ selectFirst: true });
      }));
      reset?.addEventListener('click', () => resetArchive({ focusSearch: true }));
      pickerToggle?.addEventListener('click', () => {
        const expanded = pickerToggle.getAttribute('aria-expanded') === 'true';
        if (expanded) closePicker(); else openPicker();
      });
      previous?.addEventListener('click', () => {
        if (matches.length <= 1) return;
        const currentIndex = Math.max(0, matches.findIndex((term) => term.id === activeId));
        selectTerm(matches[(currentIndex - 1 + matches.length) % matches.length].id);
      });
      next?.addEventListener('click', () => {
        if (matches.length <= 1) return;
        const currentIndex = Math.max(0, matches.findIndex((term) => term.id === activeId));
        selectTerm(matches[(currentIndex + 1) % matches.length].id);
      });
      [previous, next].forEach((button) => button?.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'mouse') requestAnimationFrame(() => button.blur());
      }, { passive: true }));

      const syncResponsivePicker = () => {
        if (!navigator || !pickerToggle) return;
        if (!isMobile()) {
          navigator.classList.remove('is-open');
          pickerToggle.setAttribute('aria-expanded', 'false');
        }
      };
      const syncKeyboardState = () => {
        const vv = window.visualViewport;
        const keyboardOpen = Boolean(input && document.activeElement === input && vv && (window.innerHeight - vv.height) > 120);
        rootEl.classList.toggle('awj-keyboard-open', keyboardOpen);
        document.body.classList.toggle('awj-keyboard-open', keyboardOpen);
      };
      const blurSearchOnPageScroll = (event) => {
        if (!input || !isMobile() || document.activeElement !== input) return;
        if (navigator?.contains(event.target)) return;
        input.blur();
      };
      input?.addEventListener('focus', syncKeyboardState, { passive: true });
      input?.addEventListener('blur', syncKeyboardState, { passive: true });
      window.visualViewport?.addEventListener('resize', syncKeyboardState, { passive: true });
      addEventListener('resize', syncResponsivePicker, { passive: true });
      document.addEventListener('touchmove', blurSearchOnPageScroll, { passive: true });

      const hashId = location.hash.startsWith('#stoff-') ? location.hash.slice(7) : '';
      render();
      selectTerm(terms.some((term) => term.id === hashId) ? hashId : activeId, { animate: false });
    });

    document.querySelectorAll('[data-awja-article]').forEach((root) => {
      if (root.dataset.awjReady === 'true') return;
      root.dataset.awjReady = 'true';
      const body = root.querySelector('[data-awja-body]');
      const toc = root.querySelector('[data-awja-toc-list]');
      if (body && toc) {
        const headings = [...body.querySelectorAll('h2, h3')];
        headings.forEach((heading, index) => {
          if (!heading.id) heading.id = `awj-chapter-${index + 1}`;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${heading.id}`;
          a.textContent = heading.textContent.trim();
          if (heading.tagName === 'H3') a.style.paddingInlineStart = '28px';
          li.appendChild(a);
          toc.appendChild(li);
        });
        const tocWrap = root.querySelector('[data-awja-toc]');
        if (!headings.length && tocWrap) tocWrap.hidden = true;
        if ('IntersectionObserver' in window && headings.length) {
          const links = [...toc.querySelectorAll('a')];
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              links.forEach((link) => link.removeAttribute('aria-current'));
              toc.querySelector(`a[href="#${CSS.escape(entry.target.id)}"]`)?.setAttribute('aria-current', 'true');
            });
          }, { rootMargin: '-20% 0px -68% 0px', threshold: 0 });
          headings.forEach((heading) => observer.observe(heading));
        }
      }
      const progress = root.querySelector('[data-awja-progress]');
      if (progress && body) {
        let ticking = false;
        const update = () => {
          const rect = body.getBoundingClientRect();
          const total = Math.max(1, body.offsetHeight - window.innerHeight * .58);
          const passed = Math.min(total, Math.max(0, -rect.top + window.innerHeight * .22));
          const ratio = passed / total;
          if (mobileViewport()) progress.style.width = `${ratio * 100}%`;
          else progress.style.height = `${ratio * 100}%`;
          ticking = false;
        };
        addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
        addEventListener('resize', update, { passive: true });
        update();
      }
    });
  });
})();
